import { Plus } from 'lucide-react';
import { nanoid } from 'nanoid';
import { memo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/shared/components/ui/button';
import { useCollectionStructureActions } from '@/features/collections/hooks/useCollectionStructureActions';
import { useDisplayedCollectionStructure } from '@/features/collections/hooks/useDisplayedCollectionStructure';
import { usePageActions } from '@/features/pages/hooks/usePageActions';
import { useCollectionStore } from '@/features/collections/stores/useCollectionStore';
import { Page } from '@/features/pages/types/page.types';
import { usePageStore } from '@/features/pages/stores/usePageStore';
import { DisplayedCollectionSection, Section } from '../../types/collection.types';

const AddSection = () => {
  const navigate = useNavigate();
  const currentCollection = useCollectionStore(state => state.currentCollection);
  const { structure: collectionStructure } = useDisplayedCollectionStructure();
  const editable = usePageStore(state => state.editable);
  const { addPage } = usePageActions();
  const { updateStructureItem } = useCollectionStructureActions();

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      const sectionId = nanoid();
      const pageInfo: Partial<Page> = { sectionId };
      if (currentCollection) {
        pageInfo.collectionId = currentCollection.id;
      }
      const newPage = await addPage({ sectionId, collectionId: currentCollection?.id });
      const newSection: Section = {
        id: sectionId,
        title: `Section ${String(collectionStructure.length + 1)}`,
        orderNumber: collectionStructure.length + 1,
      };
      const structureItem: DisplayedCollectionSection = {
        section: {
          ...newSection,
          pages: [],
        },
      };
      if (newPage && currentCollection?.id) {
        navigate(`/collection/${currentCollection.id}/page/${newPage}`);
      }
      await updateStructureItem([...collectionStructure, structureItem]);
    },
    [addPage, collectionStructure, currentCollection, navigate, updateStructureItem]
  );

  if (!editable) {
    return null;
  }

  return (
    <div className="w-full px-4 pt-1">
      <Button
        variant={'outline'}
        onClick={handleSubmit}
        className="flex items-center justify-center w-full gap-2 h-9"
      >
        <span>Add Section</span>
        <Plus className="h-4 w-4" />
      </Button>
    </div>
  );
};

export default memo(AddSection);
